import { useEffect, useState } from "react";
import {
  ActionButton,
  DataTable,
  GlassPanel,
  TurnstileWidget,
  Toast,
  UtilityButton,
  type DataColumn,
} from "@guandan/ui";
import { adminApi, type AdminOtpRequest } from "./api";
import type { AdminRow, AdminSection, AdminSession, AdminState } from "./types";

const navigation: readonly {
  section: AdminSection;
  label: string;
  mark: string;
}[] = [
  { section: "overview", label: "总览", mark: "◫" },
  { section: "allowlist", label: "邀请白名单", mark: "✓" },
  { section: "users", label: "用户", mark: "人" },
  { section: "rooms", label: "房间", mark: "房" },
  { section: "replays", label: "回放审计", mark: "▶" },
  { section: "reports", label: "举报", mark: "!" },
  { section: "reputation", label: "信誉", mark: "誉" },
  { section: "sanctions", label: "制裁", mark: "禁" },
  { section: "rules", label: "规则", mark: "规" },
  { section: "announcements", label: "公告", mark: "公" },
  { section: "audits", label: "审计日志", mark: "记" },
];

export default function AdminApp() {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [state, setState] = useState<AdminState | null>(null);
  const [section, setSection] = useState<AdminSection>("overview");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  if (!session || !state)
    return (
      <AdminLogin
        busy={busy}
        onRequest={async (email, captchaToken) => {
          setBusy(true);
          try {
            return await adminApi.requestOtp(email, captchaToken);
          } finally {
            setBusy(false);
          }
        }}
        onVerify={async (email, code) => {
          setBusy(true);
          try {
            const nextSession = await adminApi.verifyOtp(email, code);
            const nextState = await adminApi.state(nextSession.token);
            setSession(nextSession);
            setState(nextState);
          } finally {
            setBusy(false);
          }
        }}
      />
    );

  async function action(
    name: string,
    targetId: string,
    payload?: Record<string, string | number | boolean>,
  ) {
    if (!session) return;
    setBusy(true);
    try {
      const next = await adminApi.action(
        session.token,
        name,
        targetId,
        payload,
      );
      setState(next);
      setToast(`已完成：${name}`);
    } catch (cause) {
      setToast(cause instanceof Error ? cause.message : "操作失败");
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    if (!session) return;
    setBusy(true);
    try {
      await adminApi.revoke(session.token);
    } catch {
      setToast("本机已退出；服务端会话将在有效期结束后失效");
    } finally {
      setSession(null);
      setState(null);
      setBusy(false);
    }
  }

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <span>同</span>
          <div>
            <b>掼蛋高手</b>
            <small>管理与审计</small>
          </div>
        </div>
        <nav aria-label="管理模块">
          {navigation.map((item) => (
            <button
              type="button"
              key={item.section}
              aria-current={section === item.section ? "page" : undefined}
              onClick={() => setSection(item.section)}
            >
              <i aria-hidden="true">{item.mark}</i>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="admin-account">
          <small>服务端已授权</small>
          <b>{session.email}</b>
          <button type="button" disabled={busy} onClick={() => void logout()}>
            退出
          </button>
        </div>
      </aside>
      <main className="admin-main">
        <header className="admin-top">
          <div>
            <span>所有变更均写入不可变审计日志</span>
            <h1>
              {navigation.find((item) => item.section === section)?.label ??
                "管理台"}
            </h1>
          </div>
          <UtilityButton
            disabled={busy}
            onClick={() => void adminApi.state(session.token).then(setState)}
          >
            刷新数据
          </UtilityButton>
        </header>
        {section === "overview" ? (
          <Overview state={state} onOpen={setSection} />
        ) : (
          <SectionPanel
            section={section}
            state={state}
            busy={busy}
            action={action}
          />
        )}
      </main>
      {toast ? <Toast>{toast}</Toast> : null}
    </div>
  );
}

function AdminLogin({
  busy,
  onRequest,
  onVerify,
}: {
  busy: boolean;
  onRequest: (email: string, captchaToken?: string) => Promise<AdminOtpRequest>;
  onVerify: (email: string, code: string) => Promise<void>;
}) {
  const [email, setEmail] = useState(
    import.meta.env.DEV ? "admin@tongxin.local" : "",
  );
  const [sent, setSent] = useState<AdminOtpRequest | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const siteKey =
    (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim() ??
    "";
  useEffect(() => {
    if (!sent) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [sent]);
  const resendSeconds = sent
    ? Math.max(0, Math.ceil((sent.resendAt - now) / 1_000))
    : 0;
  async function request() {
    if (siteKey && !captchaToken) {
      setError("请先完成人机验证");
      return;
    }
    setError("");
    try {
      setSent(await onRequest(email, captchaToken ?? undefined));
      setCode("");
      setNow(Date.now());
      if (siteKey) {
        setCaptchaToken(null);
        setCaptchaReset((value) => value + 1);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "验证码发送失败");
    }
  }
  return (
    <main className="admin-login">
      <GlassPanel>
        <div className="admin-login__mark">同</div>
        <h1>管理台登录</h1>
        <p>
          先完成邮箱六位验证码登录，再由 Cloudflare 服务端和所有者权限共同授权。前端不持有管理员密钥。
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!sent) {
              void request();
              return;
            }
            setError("");
            void onVerify(email, code).catch((cause: unknown) =>
              setError(cause instanceof Error ? cause.message : "登录失败"),
            );
          }}
        >
          <label className="admin-field">
            <span>管理员邮箱</span>
            <input
              disabled={Boolean(sent)}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          {sent ? (
            <label className="admin-field">
              <span>六位验证码</span>
              <input
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
            </label>
          ) : null}
          {siteKey ? (
            <TurnstileWidget
              siteKey={siteKey}
              resetKey={captchaReset}
              onToken={setCaptchaToken}
              onError={setError}
            />
          ) : null}
          {sent?.developmentCode ? (
            <p className="admin-error" role="status">
              本地验证码：{sent.developmentCode}
            </p>
          ) : null}
          {error ? (
            <p className="admin-error" role="alert">
              {error}
            </p>
          ) : null}
          <ActionButton
            tone="jade"
            size="lg"
            wide
            type="submit"
            disabled={
              busy ||
              !email.includes("@") ||
              (!sent && Boolean(siteKey && !captchaToken)) ||
              (Boolean(sent) && code.length !== 6)
            }
          >
            {busy
              ? "服务端校验中…"
              : sent
                ? "验证并进入管理台"
                : "发送六位验证码"}
          </ActionButton>
        </form>
        {sent ? (
          <div className="button-row">
            <ActionButton
              tone="ghost"
              size="sm"
              disabled={
                busy || resendSeconds > 0 || Boolean(siteKey && !captchaToken)
              }
              onClick={() => void request()}
            >
              {resendSeconds > 0 ? `${resendSeconds} 秒后重发` : "重新发送"}
            </ActionButton>
            <ActionButton
              tone="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                setSent(null);
                setCode("");
                setError("");
              }}
            >
              修改邮箱
            </ActionButton>
          </div>
        ) : null}
        <small>
          {import.meta.env.DEV
            ? "本地管理服务允许：admin@tongxin.local。"
            : "生产环境通过 Cloudflare 邮箱验证码和所有者权限共同授权。"}
        </small>
      </GlassPanel>
    </main>
  );
}

function Overview({
  state,
  onOpen,
}: {
  state: AdminState;
  onOpen: (section: AdminSection) => void;
}) {
  const cards = [
    {
      section: "users" as const,
      label: "用户总数",
      value: state.users.length,
      note: "已完成首次登录",
    },
    {
      section: "allowlist" as const,
      label: "受邀邮箱",
      value: state.allowlist.filter((row) => row.enabled === true).length,
      note: "当前允许登录",
    },
    {
      section: "rooms" as const,
      label: "当前房间",
      value: state.rooms.filter((row) => row.status !== "已关闭").length,
      note: "含等待与对局",
    },
    {
      section: "reports" as const,
      label: "待处理举报",
      value: state.reports.filter((row) => row.status === "待处理").length,
      note: "需要人工复核",
    },
    {
      section: "sanctions" as const,
      label: "生效中制裁",
      value: state.sanctions.filter((row) => row.status === "生效中").length,
      note: "均有审计记录",
    },
  ];
  return (
    <div className="admin-overview">
      <section className="admin-metrics">
        {cards.map((card) => (
          <button
            type="button"
            key={card.section}
            onClick={() => onOpen(card.section)}
          >
            <span>{card.label}</span>
            <strong>{card.value}</strong>
            <small>{card.note}</small>
          </button>
        ))}
      </section>
      <div className="admin-overview-grid">
        <GlassPanel>
          <h2>系统边界</h2>
          <ul>
            <li>
              <span className="health-dot" />
              管理白名单由服务端校验
            </li>
            <li>
              <span className="health-dot" />
              浏览器无 service-role 密钥
            </li>
            <li>
              <span className="health-dot" />
              牌桌状态由 Worker 裁决
            </li>
            <li>
              <span className="health-dot" />
              全桌语音令牌短时有效
            </li>
          </ul>
        </GlassPanel>
        <GlassPanel>
          <h2>最近审计</h2>
          {state.audits.slice(0, 5).map((audit) => (
            <article className="audit-line" key={audit.id}>
              <div>
                <b>{audit.action}</b>
                <span>{audit.target}</span>
              </div>
              <time>{new Date(audit.at).toLocaleString("zh-CN")}</time>
            </article>
          ))}
        </GlassPanel>
      </div>
    </div>
  );
}

function SectionPanel({
  section,
  state,
  busy,
  action,
}: {
  section: Exclude<AdminSection, "overview">;
  state: AdminState;
  busy: boolean;
  action: (
    name: string,
    targetId: string,
    payload?: Record<string, string | number | boolean>,
  ) => Promise<void>;
}) {
  const [input, setInput] = useState("");
  const [secondary, setSecondary] = useState("");
  if (section === "audits")
    return (
      <GlassPanel className="admin-panel">
        <DataTable
          label="管理员审计日志"
          columns={[
            {
              key: "at",
              title: "时间",
              render: (row) => new Date(row.at).toLocaleString("zh-CN"),
            },
            { key: "admin", title: "管理员", render: (row) => row.admin },
            { key: "action", title: "操作", render: (row) => row.action },
            { key: "target", title: "目标", render: (row) => row.target },
            {
              key: "request",
              title: "请求 ID",
              render: (row) => row.requestId,
            },
          ]}
          rows={state.audits}
          getKey={(row) => row.id}
        />
      </GlassPanel>
    );
  const rows = state[section];
  const columns = columnsFor(section, rows, busy, action);
  return (
    <GlassPanel className="admin-panel">
      {section === "allowlist" ? (
        <form
          className="admin-command"
          onSubmit={(event) => {
            event.preventDefault();
            if (input.includes("@")) {
              void action("ALLOWLIST_ADD", input, { email: input });
              setInput("");
            }
          }}
        >
          <label className="admin-field">
            <span>添加受邀请邮箱</span>
            <input
              type="email"
              value={input}
              onChange={(event) => setInput(event.target.value)}
            />
          </label>
          <ActionButton
            tone="jade"
            type="submit"
            disabled={!input.includes("@") || busy}
          >
            允许登录
          </ActionButton>
        </form>
      ) : null}
      {section === "sanctions" ? (
        <form
          className="admin-command"
          onSubmit={(event) => {
            event.preventDefault();
            if (input) {
              void action("SANCTION_CREATE", input, {
                kind: secondary || "限时禁言",
                expires: "24 小时",
              });
              setInput("");
              setSecondary("");
            }
          }}
        >
          <label className="admin-field">
            <span>用户 ID</span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
            />
          </label>
          <label className="admin-field">
            <span>制裁类型</span>
            <select
              value={secondary}
              onChange={(event) => setSecondary(event.target.value)}
            >
              <option value="限时禁言">限时禁言</option>
              <option value="语音禁用">语音禁用</option>
              <option value="暂停匹配">暂停匹配</option>
            </select>
          </label>
          <ActionButton tone="danger" type="submit" disabled={!input || busy}>
            创建 24 小时制裁
          </ActionButton>
        </form>
      ) : null}
      {section === "announcements" ? (
        <form
          className="admin-command"
          onSubmit={(event) => {
            event.preventDefault();
            if (input.trim()) {
              void action("ANNOUNCEMENT_PUBLISH", "new", {
                title: input.trim(),
              });
              setInput("");
            }
          }}
        >
          <label className="admin-field">
            <span>公告标题</span>
            <input
              maxLength={60}
              value={input}
              onChange={(event) => setInput(event.target.value)}
            />
          </label>
          <ActionButton
            tone="blue"
            type="submit"
            disabled={!input.trim() || busy}
          >
            发布公告
          </ActionButton>
        </form>
      ) : null}
      {section === "rules" && rows[0] ? (
        <RuleEditor
          key={`${rows[0].id}-${String(rows[0].version)}`}
          rule={rows[0]}
          busy={busy}
          publish={(payload) =>
            action("RULE_PUBLISH", rows[0]?.id ?? "", payload)
          }
        />
      ) : null}
      <DataTable
        label={`${section} 数据`}
        columns={columns}
        rows={rows}
        getKey={(row) => row.id}
      />
    </GlassPanel>
  );
}

function RuleEditor({
  rule,
  busy,
  publish,
}: {
  rule: AdminRow;
  busy: boolean;
  publish: (
    payload: Record<string, string | number | boolean>,
  ) => Promise<void>;
}) {
  const [wildcard, setWildcard] = useState(
    rule.wildcardInStraightFlush === true,
  );
  const [jokerCount, setJokerCount] = useState(
    Number(rule.antiTributeBigJokerCount),
  );
  const [a2345, setA2345] = useState(rule.allowA2345Straight === true);
  const [bombSize, setBombSize] = useState(
    Number(rule.straightFlushBeatsBombSize),
  );
  return (
    <form
      className="admin-command rule-command"
      aria-label="发布权威基础规则"
      onSubmit={(event) => {
        event.preventDefault();
        void publish({
          wildcardInStraightFlush: wildcard,
          antiTributeBigJokerCount: jokerCount,
          allowA2345Straight: a2345,
          straightFlushBeatsBombSize: bombSize,
        });
      }}
    >
      <label className="admin-field checkbox-field">
        <span>红心级牌可组成同花顺</span>
        <input
          type="checkbox"
          checked={wildcard}
          onChange={(event) => setWildcard(event.target.checked)}
        />
      </label>
      <label className="admin-field">
        <span>抗贡所需大王数</span>
        <input
          type="number"
          min={0}
          max={4}
          value={jokerCount}
          onChange={(event) => setJokerCount(event.target.valueAsNumber)}
        />
      </label>
      <label className="admin-field checkbox-field">
        <span>允许 A2345 顺子</span>
        <input
          type="checkbox"
          checked={a2345}
          onChange={(event) => setA2345(event.target.checked)}
        />
      </label>
      <label className="admin-field">
        <span>同花顺压制炸弹张数</span>
        <input
          type="number"
          min={4}
          max={10}
          value={bombSize}
          onChange={(event) => setBombSize(event.target.valueAsNumber)}
        />
      </label>
      <ActionButton
        type="submit"
        tone="blue"
        disabled={
          busy ||
          !Number.isInteger(jokerCount) ||
          jokerCount < 0 ||
          jokerCount > 4 ||
          !Number.isInteger(bombSize) ||
          bombSize < 4 ||
          bombSize > 10
        }
      >
        发布为第 {Number(rule.version) + 1} 版
      </ActionButton>
    </form>
  );
}

function columnsFor(
  section: Exclude<AdminSection, "overview" | "audits">,
  rows: readonly AdminRow[],
  busy: boolean,
  action: (
    name: string,
    targetId: string,
    payload?: Record<string, string | number | boolean>,
  ) => Promise<void>,
): readonly DataColumn<AdminRow>[] {
  const preferred: Record<typeof section, readonly [string, string][]> = {
    allowlist: [
      ["email", "邮箱"],
      ["enabled", "允许登录"],
      ["role", "权限"],
      ["addedAt", "添加时间"],
    ],
    users: [
      ["nickname", "昵称"],
      ["email", "邮箱"],
      ["status", "状态"],
      ["lastSeenAt", "最近上线"],
      ["onlineSeconds", "累计上线时长"],
      ["loginCount", "登录次数"],
    ],
    rooms: [
      ["code", "房号"],
      ["owner", "房主"],
      ["status", "状态"],
      ["players", "人数"],
    ],
    replays: [
      ["status", "状态"],
      ["events", "事件数"],
      ["checksum", "校验和"],
      ["authorized", "参与者授权"],
    ],
    reports: [
      ["reporter", "举报人"],
      ["target", "目标"],
      ["reason", "原因"],
      ["status", "状态"],
    ],
    reputation: [
      ["player", "玩家"],
      ["score", "信誉分"],
      ["reason", "原因"],
      ["updatedAt", "更新时间"],
    ],
    sanctions: [
      ["target", "目标"],
      ["kind", "类型"],
      ["expires", "期限"],
      ["status", "状态"],
    ],
    rules: [
      ["name", "名称"],
      ["version", "版本"],
      ["status", "状态"],
      ["wildcardInStraightFlush", "同花顺癞子"],
      ["antiTributeBigJokerCount", "抗贡大王数"],
      ["allowA2345Straight", "A2345"],
      ["straightFlushBeatsBombSize", "同花顺压炸弹"],
      ["updatedAt", "更新时间"],
    ],
    announcements: [
      ["title", "标题"],
      ["status", "状态"],
      ["publishedAt", "发布时间"],
    ],
  };
  const available = new Set(Object.keys(rows[0] ?? {}));
  const dataColumns: DataColumn<AdminRow>[] = preferred[section]
    .filter(([key]) => available.has(key))
    .map(([key, title]) => ({
      key,
      title,
      render: (row) =>
        section === "users" && key === "lastSeenAt"
          ? formatLastSeen(Number(row[key] ?? 0))
          : section === "users" && key === "onlineSeconds"
            ? formatDuration(Number(row[key] ?? 0))
            : typeof row[key] === "boolean"
              ? row[key]
                ? "是"
                : "否"
              : String(row[key] ?? "—"),
    }));
  const actionColumn: DataColumn<AdminRow> = {
    key: "actions",
    title: "操作",
    render: (row) => {
      if (section === "allowlist")
        return (
          <div className="compact-actions">
            <ActionButton
              tone="ghost"
              size="sm"
              disabled={busy || row.role === "owner"}
              onClick={() => void action("ALLOWLIST_TOGGLE", row.id)}
            >
              {row.enabled ? "封禁" : "解封"}
            </ActionButton>
            <ActionButton
              tone="danger"
              size="sm"
              disabled={busy || row.role === "owner"}
              onClick={() => void action("ALLOWLIST_REMOVE", row.id)}
            >
              删除
            </ActionButton>
          </div>
        );
      if (section === "users")
        return (
          <ActionButton
            tone="danger"
            size="sm"
            disabled={busy}
            onClick={() =>
              void action("SANCTION_CREATE", row.id, {
                kind: "暂停匹配",
                expires: "24 小时",
              })
            }
          >
            暂停匹配 24h
          </ActionButton>
        );
      if (section === "rooms")
        return (
          <ActionButton
            tone="danger"
            size="sm"
            disabled={busy || row.status === "已关闭"}
            onClick={() => void action("ROOM_CLOSE", row.id)}
          >
            关闭房间
          </ActionButton>
        );
      if (section === "replays")
        return (
          <ActionButton
            tone="blue"
            size="sm"
            disabled={busy}
            onClick={() => void action("REPLAY_VERIFY", row.id)}
          >
            校验事件链
          </ActionButton>
        );
      if (section === "reports")
        return (
          <ActionButton
            tone="jade"
            size="sm"
            disabled={busy || row.status === "已处理"}
            onClick={() => void action("REPORT_RESOLVE", row.id)}
          >
            标记已处理
          </ActionButton>
        );
      if (section === "reputation")
        return (
          <div className="compact-actions">
            <ActionButton
              tone="jade"
              size="sm"
              disabled={busy}
              onClick={() =>
                void action("REPUTATION_ADJUST", row.id, {
                  delta: 1,
                  reason: "人工复核恢复",
                })
              }
            >
              +1
            </ActionButton>
            <ActionButton
              tone="danger"
              size="sm"
              disabled={busy}
              onClick={() =>
                void action("REPUTATION_ADJUST", row.id, {
                  delta: -1,
                  reason: "人工复核扣减",
                })
              }
            >
              -1
            </ActionButton>
          </div>
        );
      if (section === "sanctions")
        return (
          <ActionButton
            tone="ghost"
            size="sm"
            disabled={busy || row.status === "已撤销"}
            onClick={() => void action("SANCTION_REVOKE", row.id)}
          >
            撤销
          </ActionButton>
        );
      if (section === "rules") return <span>新房自动采用</span>;
      return <span>已发布</span>;
    },
  };
  return [...dataColumns, actionColumn];
}

function formatLastSeen(value: number): string {
  return value > 0 ? new Date(value).toLocaleString("zh-CN") : "尚未上线";
}

function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (hours > 0) return `${hours} 小时 ${minutes} 分钟`;
  if (minutes > 0) return `${minutes} 分钟`;
  return `${seconds} 秒`;
}
